import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  FeeScheduleEntityType,
  FeeScheduleType,
  PaymentSystemType,
  useGetAllClientsQuery,
  useGetFeeSchedulesLazyQuery,
  useGetPartnershipsQuery,
} from '../../types';
import { fetchAllPages } from '../../utils/fetch-all-pages';
import { FeeScheduleFilterParams } from './fee-schedule-filter-bar';

export interface FeeScheduleRow extends FeeScheduleType {
  entityName: string;
}

export interface FeeScheduleQueriesResult {
  rows: FeeScheduleRow[];
  loading: boolean;
  hasError: boolean;
  refetch: () => void;
}

interface FilterVariables {
  enabled?: boolean;
  entityIds?: number[];
  paymentSystems?: PaymentSystemType[];
  paymentTypes?: string[];
  priceMin?: number;
  priceMax?: number;
}

const emptyItems: FeeScheduleType[] = [];

export const useFeeScheduleQueries = (
  entityType: FeeScheduleEntityType,
  filterParams?: FeeScheduleFilterParams,
): FeeScheduleQueriesResult => {
  const [getFeeSchedules] = useGetFeeSchedulesLazyQuery();

  const [feeScheduleItems, setFeeScheduleItems] =
    useState<FeeScheduleType[]>(emptyItems);
  const [fetchLoading, setFetchLoading] = useState(false);
  const [fetchError, setFetchError] = useState(false);
  const [refreshCount, setRefreshCount] = useState(0);

  const refetch = useCallback((): void => {
    setRefreshCount(prev => prev + 1);
  }, []);

  useEffect(() => {
    const controller = new AbortController();

    const filterVariables: FilterVariables = {
      ...(filterParams?.status === 'enabled' && { enabled: true }),
      ...(filterParams?.status === 'disabled' && { enabled: false }),
      ...(filterParams?.entityIds &&
        filterParams.entityIds.length > 0 && {
          entityIds: filterParams.entityIds,
        }),
      ...(filterParams?.paymentSystems &&
        filterParams.paymentSystems.length > 0 && {
          paymentSystems: filterParams.paymentSystems as PaymentSystemType[],
        }),
      ...(filterParams?.paymentTypes &&
        filterParams.paymentTypes.length > 0 && {
          paymentTypes: filterParams.paymentTypes,
        }),
      ...(filterParams?.priceMin !== undefined &&
        filterParams?.priceMin !== '' && {
          priceMin: Number(filterParams.priceMin),
        }),
      ...(filterParams?.priceMax !== undefined &&
        filterParams?.priceMax !== '' && {
          priceMax: Number(filterParams.priceMax),
        }),
    };

    const fetchPage = async (
      page: number,
      pageSize: number,
    ): Promise<{ items: FeeScheduleType[]; total: number } | undefined> => {
      const result = await getFeeSchedules({
        variables: {
          entityType,
          page,
          size: pageSize,
          ...filterVariables,
        },
        fetchPolicy: refreshCount > 0 ? 'network-only' : 'cache-first',
        context: { fetchOptions: { signal: controller.signal } },
      });
      if (result.error) throw result.error;
      const data = result.data?.getFeeSchedules;
      return data ? { items: data.items, total: data.total } : undefined;
    };

    const fetchAll = async (): Promise<void> => {
      setFetchLoading(true);
      setFetchError(false);
      try {
        const allItems = await fetchAllPages(fetchPage);
        if (!controller.signal.aborted) {
          setFeeScheduleItems(allItems);
        }
      } catch {
        if (!controller.signal.aborted) {
          setFetchError(true);
        }
      } finally {
        if (!controller.signal.aborted) {
          setFetchLoading(false);
        }
      }
    };

    fetchAll();
    return (): void => {
      controller.abort();
    };
  }, [
    entityType,
    filterParams?.status,
    filterParams?.entityIds,
    filterParams?.paymentSystems,
    filterParams?.paymentTypes,
    filterParams?.priceMin,
    filterParams?.priceMax,
    getFeeSchedules,
    refreshCount,
  ]);

  const {
    data: clientsData,
    loading: clientsLoading,
    error: clientsError,
  } = useGetAllClientsQuery({
    skip: entityType !== FeeScheduleEntityType.Client,
  });

  const {
    data: partnershipsData,
    loading: partnershipsLoading,
    error: partnershipsError,
  } = useGetPartnershipsQuery({
    variables: { type: 'billing' },
    skip: entityType !== FeeScheduleEntityType.Partnership,
  });

  const entityNameMap = useMemo(() => {
    const map = new Map<number, string>();

    if (
      entityType === FeeScheduleEntityType.Client &&
      clientsData?.getAllClients
    ) {
      clientsData.getAllClients.forEach(client => {
        map.set(client.id, client.name);
      });
    }

    if (
      entityType === FeeScheduleEntityType.Partnership &&
      partnershipsData?.getPartnerships
    ) {
      partnershipsData.getPartnerships.forEach(partnership => {
        map.set(partnership.id, partnership.name);
      });
    }

    return map;
  }, [entityType, clientsData, partnershipsData]);

  const rows: FeeScheduleRow[] = useMemo(
    () =>
      feeScheduleItems.map(item => ({
        ...item,
        entityName:
          entityNameMap.get(item.entityId) ?? `Unknown (${item.entityId})`,
      })),
    [feeScheduleItems, entityNameMap],
  );

  const loading =
    fetchLoading ||
    (entityType === FeeScheduleEntityType.Client && clientsLoading) ||
    (entityType === FeeScheduleEntityType.Partnership && partnershipsLoading);

  const hasError = fetchError || !!clientsError || !!partnershipsError;

  return { rows, loading, hasError, refetch };
};
